import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { GlobalConfigAPI } from '@martinus/global-config/api/GlobalConfigAPI.js';
import { SGConfig } from '@martinus/global-config/db/sg-config.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';

const loadingSkeleton = html`
  <div class="card p-4 m-4">
    <div class="card-body">
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
    </div>
  </div>
`;

const susConfigStatusCards = {
  activated: html`
    <div class="card bg-success/20 p-4 m-4">
      <span class="font-bold text-success"> Das Anmeldeformular ist aktiviert und freigegeben! </span>
    </div>
  `,
  deactivated: html`
    <div class="card bg-warning/20 p-4 m-4">
      <span class="font-bold text-warning"> Das Anmeldeformular ist nicht aktiviert! </span>
    </div>
  `,
};

export const SGGeneralConfig = stateful(() => {
  const { value: config, loading: configLoading } = useQuery(
    () => GlobalConfigAPI.subscribe()(selectionSet(() => SGConfig, 3)),
    [],
  );

  if (configLoading || !config) {
    return loadingSkeleton;
  }

  const form = useForm(config, { validator: null });

  const handleSave = async () => {
    try {
      await form.uploadAllPendingFiles();
      await form.document.save();
      void SgSnackbar.show('Konfiguration gespeichert');
    } catch (error) {
      if (error instanceof Error) {
        await alertDialog(error.message);
      }
    }
  };

  const autoSaveMiddleWare = <T extends string | boolean>(
    fieldProps: FunctionalFormFieldProps<T>,
  ): FunctionalFormFieldProps<T> => {
    return {
      ...fieldProps,
      onChanged: async value => {
        fieldProps.onChanged?.(value);

        // TODO there is some timing issue with how the fileInput registers the file in the form and the upload. So we delay it a bit here
        // Needs a fix in engine on the fileinput
        setTimeout(async () => await handleSave(), 200);
      },
    };
  };

  return html`
    <div class="card-container">
      <div class="card card-body">
        ${CardHeader({ title: 'Herunterladbare Dateien' })}

        <p class="col-span-full mb-6">
          Diese Dokumente werden an verschiedenen Stellen in der Platform den Nutzern zur Verfügung gestellt.
        </p>

        <div class="form-grid">
          ${FormGridEntry({
            label: 'KZVK',
            render: FileInput({
              ...autoSaveMiddleWare(form.field('kzvkID')),
              placeholder: 'Keine KZVK angegeben',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Kirchliches Amtsblatt',
            render: FileInput({
              ...autoSaveMiddleWare(form.field('kirchlichesAmtsblattID')),
              placeholder: 'Keine Kirchliches Amtsblatt angegeben',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Kirchliches Datenschutzgesetz',
            render: FileInput({
              ...autoSaveMiddleWare(form.field('kirchlichesDatenschutzgesetzID')),
              placeholder: 'Keine Kirchliches Datenschutzgesetz angegeben',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Infos für Mitarbeiter',
            render: FileInput({
              ...autoSaveMiddleWare(form.field('infosFuerMitarbeiterID')),
              placeholder: 'Keine Infos für Mitarbeiter angegeben',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Infoblatt Infektionsschutz',
            render: FileInput({
              ...autoSaveMiddleWare(form.field('infectionInformationPaper')),
              placeholder: 'Kein Infoblatt Infektionsschutz angegeben',
              placeholderMode: 'hide',
            }),
          })}
        </div>
      </div>

      <div class="card card-body">
        ${CardHeader({ title: 'SuS Anmeldeformular' })}

        <div class="form-grid">
          ${FormGridEntry({
            label: 'Anmeldeformular aktivieren',
            render: html`
              <div class="flex flex-row justify-between items-center">
                ${ToggleInput({ ...autoSaveMiddleWare(form.field('activatedSUSForm')) })}
                ${config.activatedSUSForm ? susConfigStatusCards.activated : susConfigStatusCards.deactivated}
              </div>
            `,
          })}
        </div>
      </div>
    </div>
  `;
});
